import React from 'react'
import { Button, Input, Label } from '@/shared/ui'
import StayLoggedInCheckbox from '../components/StayLoggedInCheckbox'
import { ArrowRight, Eye, EyeOff, Mail, Lock } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { signInWithPassword } from '@/modules/auth/services/authService'
import { supabase } from '@/lib/supabase'
import { useNavigate, useLocation } from 'react-router-dom'
import bgVideo from '@/images/video/video-pglogin.mp4'
import { getLogo } from '@/modules/branding/brandingService'
import { isLightTheme } from '@/lib/theme'
import ForgotPasswordModal from '../components/ForgotPasswordModal'

function PasswordInput({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  const [show, setShow] = React.useState(false)
  return (
    <div className="relative">
      <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Digite sua senha"
        className="rounded-md border-border/60 bg-muted/30 pl-9 pr-9"
      />
      <button
        type="button"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/70 hover:text-foreground"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

async function computeDestForUser(u: any, location: any): Promise<string> {
  const MASTER_EMAIL = (import.meta.env.VITE_MASTER_EMAIL as string) || 'rgo.mendes@gmail.com'
  let dest: string = '/profile'

  const uid = u?.id
  const userMetaRole = String(
    (u as any)?.user_metadata?.role ||
    (u as any)?.app_metadata?.role ||
    ''
  )
    .trim()
    .toLowerCase()

  const emailIsMaster = (u?.email || '').toLowerCase() === MASTER_EMAIL.toLowerCase()

  if (emailIsMaster) {
    dest = '/dashboard-master'
  } else if (userMetaRole === 'afiliado' || userMetaRole === 'affiliate') {
    dest = '/dashboard-affiliate'
  } else if (uid) {
    const { data: prof } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', uid)
      .maybeSingle()
    const roleNorm = String(((prof as any)?.role as string | undefined) || '')
      .trim()
      .toLowerCase()
    if (roleNorm === 'master') dest = '/dashboard-master'
    else if (roleNorm === 'afiliado' || roleNorm === 'affiliate') dest = '/dashboard-affiliate'
    else dest = '/profile'
  } else {
    dest = '/profile'
  }

  // Preferir "from" somente quando for uma rota válida e não causar loop
  

  return dest
}

export default function LoginPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation() as any
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)
  const [showForgot, setShowForgot] = React.useState(false)

  React.useEffect(() => {
    if (!user) return
    ;(async () => {
      const dest = await computeDestForUser(user, location)
      if (location?.pathname !== dest) {
        navigate(dest, { replace: true })
      }
    })()
  }, [user])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSubmitting(true)
      const { data, error } = await signInWithPassword(email.trim(), password)
      if (error) throw error

      const dest = await computeDestForUser(data.user, location)
      navigate(dest, { replace: true })
    } catch (err: any) {
      setError(err?.message ?? 'Falha no login')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="grid h-[100dvh] min-h-[100dvh] grid-cols-1 overflow-hidden sm:grid-cols-2">
      <div className="relative hidden h-full sm:block">
        <video
          src={bgVideo as unknown as string}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        />
        <ForgotPasswordModal open={showForgot} onOpenChange={setShowForgot} />
      </div>

      <div className="relative flex h-full items-center justify-center px-5 py-6 sm:px-12 sm:py-12">
        {/* Mobile: vídeo de fundo com fade escuro para contraste */}
        <div className="absolute inset-0 sm:hidden">
          <video
            src={bgVideo as unknown as string}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
        </div>
        <div className="relative z-10 w-full max-w-md">
          <LoginLogo />
          <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">Bem-vindo!</h1>


          <form className="mt-4 space-y-4 sm:mt-5 sm:space-y-5" onSubmit={onSubmit}>
            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="after:ml-1 after:text-destructive after:content-['*']">E-mail</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Digite seu e-mail"
                  className="rounded-md border-border/60 bg-muted/30 pl-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="after:ml-1 after:text-destructive after:content-['*']">Senha</Label>
              <PasswordInput id="password" value={password} onChange={setPassword} />
            </div>

            <StayLoggedInCheckbox className="pt-1" />

            <Button type="submit" disabled={submitting} className="h-11 w-full justify-center gap-2 bg-primary text-primary-foreground disabled:opacity-60">
              <span>{submitting ? 'Entrando...' : 'Entrar'}</span>
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </form>

          <div className="mt-4 text-right text-xs text-foreground/70">
            <a href="#" className="text-primary hover:underline" onClick={(e)=>{ e.preventDefault(); setShowForgot(true) }}>Esqueceu a senha?</a>
          </div>
        </div>
      </div>
    </div>
  )
}

function LoginLogo() {
  const [url, setUrl] = React.useState<string | null>(null)
  React.useEffect(() => {
    ;(async () => setUrl(await getLogo('login', isLightTheme())))()
  }, [])
  if (!url) return null
  return (
    <div className="w-full">
      <img src={url} alt="logo" className="mx-auto mb-10 block h-[56px] w-auto sm:mb-[70px] sm:h-[65px]" />
    </div>
  )
}

// (inline) Modal de recuperação acoplado à página de Login

